import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../auth/session';

/**
 * The server's clock and UTC offset. Every time the terminals show is platform server time (what bars, sessions, daily
 * limits and "Today" expiry use), so a trader sees one clock everywhere.
 */
function useServerInfo() {
  const api = useApi();
  return useQuery({
    queryKey: ['server-info'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/server-info');
      if (!data) throw new Error(`server-info failed: ${JSON.stringify(error)}`);
      return {
        serverTimeMs: data.serverTimeMs ?? 0,
        offsetMinutes: data.serverUtcOffsetMinutes ?? 0,
        feedUp: data.feedUp ?? false,
        fetchedAt: Date.now(),
      };
    },
    refetchInterval: 60_000,
  });
}

/** Platform server time (what bars and sessions use), kept in step with the server and ticking locally. */
export function useServerTime(): Date | null {
  const info = useServerInfo();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);
  return info.data ? new Date(info.data.serverTimeMs + (now - info.data.fetchedAt)) : null;
}

/** The server's offset from UTC in minutes, or null until it is known. */
export function useServerOffset(): number | null {
  return useServerInfo().data?.offsetMinutes ?? null;
}

const two = (n: number) => String(n).padStart(2, '0');

export function formatServerTime(date: Date): string {
  // The server timestamp already has the platform offset applied, so UTC getters give the server's wall clock.
  return `${two(date.getUTCHours())}:${two(date.getUTCMinutes())}:${two(date.getUTCSeconds())}`;
}

// .NET sends up to seven fractional digits; trim to milliseconds so every browser parses it.
const toMs = (at: string | number): number =>
  typeof at === 'number' ? at : Date.parse(at.replace(/(\.\d{3})\d+/, '$1'));

/** An offset in minutes, or one that depends on the instant (local time and daylight saving). */
export type Offset = number | ((ms: number) => number);
const offsetAt = (offset: Offset, ms: number) => (typeof offset === 'number' ? offset : offset(ms));

/** A UTC instant (ISO string or epoch ms) as wall-clock time at `offset`: "2026-09-20 17:05:09". Empty for no time. */
export function formatServerDateTime(at: string | number | null | undefined, offsetMinutes: Offset): string {
  if (at === null || at === undefined || at === '') return '';
  const ms = toMs(at);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms + offsetAt(offsetMinutes, ms) * 60_000);
  return `${d.getUTCFullYear()}-${two(d.getUTCMonth() + 1)}-${two(d.getUTCDate())} ${formatServerTime(d)}`;
}

/** Only the server wall-clock time of day: "17:05:09". */
export function formatServerClock(at: string | number, offsetMinutes: Offset): string {
  const ms = toMs(at);
  return formatServerTime(new Date(ms + offsetAt(offsetMinutes, ms) * 60_000));
}

/** A UTC instant as the value of a datetime-local input showing server time ("2026-09-20T17:05"). */
export function toServerInput(at: string | number, offsetMinutes: Offset): string {
  return formatServerDateTime(at, offsetMinutes).slice(0, 16).replace(' ', 'T');
}

/** A datetime-local value read as server time, back to a UTC ISO instant; null when it is not a valid date-time. */
export function fromServerInput(value: string, offsetMinutes: Offset): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) return null;
  const ms = Date.parse(`${value.length === 16 ? `${value}:00` : value}Z`);
  // the wall-clock value read as UTC is within a day of the instant: close enough to pick the offset in force
  return Number.isNaN(ms) ? null : new Date(ms - offsetAt(offsetMinutes, ms) * 60_000).toISOString();
}

/** A label for the offset: "UTC+3", "UTC−2:30", "UTC". */
export function serverOffsetLabel(offsetMinutes: number): string {
  if (offsetMinutes === 0) return 'UTC';
  const sign = offsetMinutes > 0 ? '+' : '−';
  const abs = Math.abs(offsetMinutes);
  return `UTC${sign}${Math.floor(abs / 60)}${abs % 60 ? `:${two(abs % 60)}` : ''}`;
}
